import React, {useCallback, useEffect, useRef, useState} from 'react';
import { z } from 'zod';
import { useFieldArray, useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useLocation } from 'react-router-dom';
import { Col, Row, FormControl, Form } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';
import Axios from 'axios';
import classnames from 'classnames';
import { FormGroup, Input, Label } from 'reactstrap';
import { useDispatch } from 'react-redux';
import { useSearchContext } from '../../../../../shared/contexts/SearchContextProvider';
import { useAccessibilityContext } from '../../../../consulta-acessibilidade/contexts/AccessibilityContext';
import { limparFiltros, changeAcervosFiltrados } from '../../../../../store/ducks/pesquisa';
import { BibliotecaAPI } from '../../../../../shared/http-requests';
import { APIHelper, StringHelper } from '../../../../../shared/helpers';
import AppInfo from '../../../../../shared/components/AppInfo';
import AutoCompleteSuggestions from '../AutoCompleteSuggestions';
import Button from '../../../../consulta-acessibilidade/Button';
import AdvancedSearchFilters from './AdvancedSearchFilters';
import { useAppContext } from '../../../../../shared/contexts/AppContextProvider';
import eventBus from '../../../../../shared/events/GlobalEvent';
import { faMinus, faPlus } from '@fortawesome/free-solid-svg-icons';
import { IconProp } from '@fortawesome/fontawesome-svg-core';

interface AdvancedSearchProps {
    autoCompleteTerms: any;
    loading: boolean;
    onChangeSearch: (data: {searchTerm: string, searchOption: string}, index: number) => void
    // eslint-disable-next-line react/require-default-props
    error?: any
}

const schema = z.object({
    items: z.array(
        z.object({
            input: z.string().min(1, "CampoBuscaObrigatorio"),
            option: z.string().min(1, "É necessario informar a opção de pesquisa."),
            condition: z.string().optional(),
        })
    ),
    termType: z.enum(["P", "E", "I"], {
        required_error: "Você deve selecionar uma opção", // Mensagem de erro se não for selecionado
        invalid_type_error: "Tipo inválido"
    }),
});

type FormData = z.infer<typeof schema>;

const AdvancedSearch: React.FC<AdvancedSearchProps> = ({autoCompleteTerms, loading, error, onChangeSearch}) => {

    const dispatch = useDispatch();
    const [isOpenFilters, setIsOpenFilters] = useState<boolean>(false)
    const location = useLocation();
    const { search, loadingSearch, lastSearchForm, researchDone } = useSearchContext();
    const searchParams = new URLSearchParams(location.search)
    const {t } = useTranslation();
    const [filtersOptionsInitialData, setFilterOptionsInitialData] = useState<any>()
    const {
        control,
        register,
        handleSubmit,
        watch,
        reset,
        formState: { errors },
    } = useForm<FormData>({
        resolver: zodResolver(schema),
        defaultValues: {
            items: [{ input: '', option: 'LIVRE', }],
            termType: 'P'
        }
    });

    const data = watch()

    const handleClearSearch = useCallback(() => {
        reset({ items: [{ input: '', option: 'LIVRE', }], termType: 'P'})
        setFilterOptionsInitialData({
            libraries: [],
            typeOfWork: [],
            collection: [],
            publicationPlace: undefined,
            language: undefined,
            internLocation: undefined,
            physicalDesc: undefined,
            order: undefined,
            itemsPerPage: undefined,
            publicationYear: undefined,
            compPublicationYear: undefined,
            virtualLib: 'N',
            finalPublicationYear : undefined
        })
    }, [setFilterOptionsInitialData])

    // Escutando o evento global
    useEffect(() => {
        eventBus.on("CLEAR_SEARCH", handleClearSearch);
        // Cleanup: Remover o evento ao desmontar o componente
        return () => {
            eventBus.off("CLEAR_SEARCH", handleClearSearch);
        };
    }, [reset, handleClearSearch]);

    useEffect(() => {
        if(lastSearchForm){
            if(lastSearchForm.termo_pesquisa){
                const defaultValues: FormData = {
                    items: [],
                    termType: 'E'
                }
                defaultValues.items.push({
                    input: lastSearchForm.termo_pesquisa || '',
                    option: lastSearchForm.coluna_um || '',
                    condition: lastSearchForm.condicao_um || undefined,
                })
                if(lastSearchForm.termo_pesquisa_dois){
                    defaultValues.items.push({
                        input: lastSearchForm.termo_pesquisa_dois || '',
                        option: lastSearchForm.coluna_dois || '',
                        condition: lastSearchForm.condicao_dois || undefined,
                    })
                }
                if(lastSearchForm.termo_pesquisa_tres){
                    defaultValues.items.push({
                        input: lastSearchForm.termo_pesquisa_tres || '',
                        option: lastSearchForm.coluna_tres || '',
                    })
                }
                reset(defaultValues)
            }
            let initialData = {};
            let hasFilterOptions = false;

            if(lastSearchForm?.bib_virtual){
                hasFilterOptions = true
                initialData = {
                    ...initialData,
                    bib_virtual: lastSearchForm?.bib_virtual
                }
            }
            if(lastSearchForm?.biblioteca){
                hasFilterOptions = true
                initialData = {
                    ...initialData,
                    libraries: lastSearchForm?.biblioteca?.trim().replace(/,$/, '').split(',')
                }
            }
            if(lastSearchForm?.cod_tipo_obra){
                hasFilterOptions = true
                initialData = {
                    ...initialData,
                    typeOfWork: lastSearchForm.cod_tipo_obra.trim().replace(/,$/, '').split(','),
                }
            }
            if(lastSearchForm?.colecao){
                hasFilterOptions = true
                initialData = {
                    ...initialData,
                    collection: lastSearchForm.colecao.trim().replace(/,$/, '').split(','),
                }
            }
            if(lastSearchForm?.local){
                hasFilterOptions = true
                initialData = {
                    ...initialData,
                    publicationPlace: lastSearchForm.local,
                }
            }
            if(lastSearchForm?.idioma){
                hasFilterOptions = true
                initialData = {
                    ...initialData,
                    language: lastSearchForm.idioma,
                }
            }
            if(lastSearchForm?.local_interna){
                hasFilterOptions = true
                initialData = {
                    ...initialData,
                    internLocation: lastSearchForm.local_interna,
                }
            }
            if(lastSearchForm?.categoria){
                hasFilterOptions = true
                initialData = {
                    ...initialData,
                    physicalDesc: lastSearchForm.categoria,
                }
            }
            if(lastSearchForm?.ordenacao){
                hasFilterOptions = true
                initialData = {
                    ...initialData,
                    order: lastSearchForm.ordenacao,
                }
            }
            if(lastSearchForm?.perPage){
                hasFilterOptions = true
                initialData = {
                    ...initialData,
                    itemsPerPage: lastSearchForm.perPage,
                }
            }
            if(lastSearchForm?.ano_publicacao){
                hasFilterOptions = true
                initialData = {
                    ...initialData,
                    publicationYear: lastSearchForm.ano_publicacao,
                }
            }
            if(lastSearchForm?.comp_ano_publicacao){
                hasFilterOptions = true
                initialData = {
                    ...initialData,
                    compPublicationYear: lastSearchForm.comp_ano_publicacao,
                }
            }
            if(lastSearchForm?.ano_publicacao_final){
                hasFilterOptions = true
                initialData = {
                    ...initialData,
                    finalPublicationYear: lastSearchForm.ano_publicacao_final,
                }
            }
            setFilterOptionsInitialData(initialData)
            setIsOpenFilters(hasFilterOptions)
        }else{
            handleClearSearch()
        }

    }, [lastSearchForm, reset]);


    const [filterOptions, setFilterOptions] = useState<any>({})
    const items = watch('items')
    const { fields, append, remove } = useFieldArray({
        control,
        // @ts-ignore
        name: "items",
    });
    const { searchInputRef } = useAccessibilityContext();
    const formRef = useRef(null);


    const conditions = [
        { label: t('AND'), value: 'AND', intermediario: false },
        { label: t('OR'), value: 'OR', intermediario: false },
        { label: t('NOT'), value: 'NOT', intermediario: false },
        { label: t('AFT'), value: 'AFT', intermediario: true },
        { label: t('BEF'), value: 'BEF', intermediario: true },
        { label: t('PRO'), value: 'PRO', intermediario: true },
    ];
    const options = [
        { label: t('LIVRE'), value: 'LIVRE' },
        { label: t('TITULO'), value: 'TITULO' },
        { label: t('ASSUNTO'), value: 'ASSUNTO' },
        { label: t('AUTOR'), value: 'AUTOR' },
    ];
    const handleSearch = async(data: FormData) =>{
        dispatch(limparFiltros());
        dispatch(changeAcervosFiltrados([]));

        const form: any = {};
        const map_keys : any = {
            input: [
                'termo_pesquisa',
                'termo_pesquisa_dois',
                'termo_pesquisa_tres'
            ],
            condition: {
                1: 'condicao_um',
                2:'condicao_dois',
            },
            column: [
                'coluna_um',
                'coluna_dois',
                'coluna_tres'
            ]
        }
        const map_keys_params : any = {
            input: [
                'q',
                'q2',
                'q3'
            ],
            condition: {
                1: 'condition',
                2:'condition2',
            },
            column: [
                'for',
                'for2',
                'for3'
            ]
        }
        const params: any = {};

        data.items.forEach((item, index) => {
            form[map_keys.input[index]] = item.input;
            form[map_keys.column[index]] = item.option;

            params[map_keys_params.input[index]] = item.input;
            params[map_keys_params.column[index]] = item.option;

            if(item.condition){
                form[map_keys.condition[index]] = item.condition;
                params[map_keys_params.condition[index]] = item.condition;
            }
        })


        form.biblioteca = filterOptions.libraries?.length ? filterOptions.libraries.join(',')+',' : filterOptions.libraries;
        if(form.biblioteca)
            params.id_biblioteca = form.biblioteca;

        form.cod_tipo_obra = filterOptions.typeOfWork?.length ? filterOptions.typeOfWork.join(',')+',' : filterOptions.typeOfWork;
        if(form.cod_tipo_obra)
            params.tipo_obra = form.cod_tipo_obra;

        form.colecao = filterOptions.collection?.length ? filterOptions.collection.join(',') : filterOptions.collection;
        if(form.colecao)
            params.id_colecao = form.colecao;

        form.tipo_termos = data.termType
        if(form.tipo_termos)
            params.keyword_type = form.tipo_termos;

        form.ano_publicacao = filterOptions.publicationYear
        if(form.ano_publicacao)
            params.ano_publicacao = form.ano_publicacao;

        form.ano_publicacao_final = filterOptions.finalPublicationYear
        if(form.ano_publicacao_final)
            params.ano_publicacao_final = form.ano_publicacao_final;

        form.comp_ano_publicacao = filterOptions.compPublicationYear
        if(form.comp_ano_publicacao)
            params.comp_ano_publicacao = form.comp_ano_publicacao;

        form.idioma = filterOptions.language
        if(form.idioma)
            params.idioma = form.idioma;

        form.local = filterOptions.publicationPlace // cod_local
        if(form.local)
            params.local = form.local;

        form.categoria = filterOptions.physicalDesc //cod_categ
        if(form.categoria)
            params.categoria = form.categoria;

        form.ordenacao = filterOptions.order
        if(form.ordenacao)
            params.ordenacao = form.ordenacao;

        form.bib_virtual = filterOptions.virtualLib
        if(form.bib_virtual)
            params.cr = form.bib_virtual;

        form.perPage = filterOptions.itemsPerPage
        if(form.perPage)
            params.perPage = form.perPage;

        form.local_interna = filterOptions.internLocation
        if(form.local_interna)
            params.local_interna = form.local_interna;

        await search(form, params);
    }


    return (
        <Form
            ref={formRef}
            onSubmit={handleSubmit(handleSearch)}
        >
            <Row className="content__left__input px-3">
                <Col md="10" xs="12">
                    {
                        fields.map((field, index) => (
                            <div key={field.id}>
                                <Row
                                    className={`w-100 ${index !== 0 &&
                                    'additional_search_input pl-sm-0 pl-2 pr-sm-0 pr-2'}`}
                                    key={`row-termo-${index}`}
                                >
                                    <Col sm="1" xs="12" className="input-holder mt-3 text-sm-right text-center">
                                        {(index > 0) && (
                                            <div>
                                                <button
                                                    className="btn-generic--primeiro nao d-sm-inline-block d-none mt-3"
                                                    title={t('Remover')}
                                                    type="button"
                                                    onClick={() => remove(index)}
                                                >
                                                    <FontAwesomeIcon icon="times" />
                                                </button>
                                                <button
                                                    style={{ marginTop: '-30px' }}
                                                    className="btn-generic--primeiro nao d-sm-none d-block"
                                                    type="button"
                                                    onClick={() => remove(index)}
                                                >
                                                    <FontAwesomeIcon
                                                        icon="times"
                                                        className="s15 mr-2" />
                                                    <span className="s14">
                                                {' '}
                                                        {t('Remover')}{' '}
                                            </span>
                                                </button>
                                            </div>
                                        )}
                                    </Col>
                                    <Col
                                        sm="2"
                                        xs="12"
                                        className="input-holder mt-3"
                                    >
                                        <div className="rotulo">
                                            {t('TipoPesquisas')}
                                        </div>
                                        <Controller
                                            control={control}
                                            name={`items.${index}.option`}
                                            render={({ field }) => (
                                                <FormControl
                                                    as="select"
                                                    className='form-control'
                                                    value={field.value}
                                                    onChange={(e) => {
                                                        if(onChangeSearch){
                                                            const { value } = e.target;
                                                            const payload = {
                                                                searchTerm: data.items[index].input,
                                                                searchOption: value
                                                            }
                                                            onChangeSearch(payload, index);
                                                        }
                                                        return field.onChange(e);
                                                    }}
                                                    isInvalid={errors?.items?.[index]?.option !== undefined}
                                                >
                                                    {options.map(item => (
                                                        <option
                                                            key={`opt-${item.value}`}
                                                            value={item.value}
                                                        >
                                                            {item.label}
                                                        </option>
                                                    ))}
                                                </FormControl>

                                            )}
                                        />
                                        {errors.items?.[index]?.option  ? <FormControl.Feedback type="invalid">{t(errors.items[index].option.message as any)}</FormControl.Feedback> : ''}
                                    </Col>
                                    <Col
                                        sm="6"
                                        xs="12"
                                        className="input-holder mt-3"
                                    >
                                        <div className='rotulo'>
                                            {t('CampoBusca')}
                                        </div>
                                        <Row>
                                            <Col xs='10' md='11'>
                                                <Controller
                                                    control={control}
                                                    name={`items.${index}.input`}
                                                    render={({ field }) => (
                                                        <FormControl
                                                            value={field.value}
                                                            onChange={(e) => {
                                                                if(onChangeSearch){
                                                                    const { value } = e.target;
                                                                    const payload = {
                                                                        searchTerm: value,
                                                                        searchOption: data.items[index].option
                                                                    }
                                                                    onChangeSearch(payload, index);
                                                                }
                                                                return field.onChange(e);
                                                            }}
                                                            isInvalid={errors?.items?.[index]?.input !== undefined}
                                                            ref={(e: any) => {
                                                                // register(`items.${index}.input`).ref(e);
                                                                searchInputRef.current = e;
                                                            }}
                                                            type="search"
                                                            className="rad10 b-tema20 cortxt foco form-control"
                                                            placeholder={t('DigiteSuaBuscaAqui')}
                                                            autoComplete="off"
                                                            list={`suggestions-pesquisa-avancada-${index}`}
                                                        />
                                                    )}
                                                />
                                                {errors.items?.[index]?.input  ? <FormControl.Feedback type="invalid">{t(errors.items[index].input.message as any)}</FormControl.Feedback> : ''}
                                            </Col>
                                            {
                                                index === 0 ? (
                                                    <Col className='d-flex justify-content-start align-items-center' xs='2' md='1'>
                                                        <AppInfo message={t('MsgHelpIcon')} />
                                                    </Col>
                                                ) : ''
                                            }
                                        </Row>
                                        {/* @ts-ignore */}
                                        <AutoCompleteSuggestions
                                            autoCompleteTerms={autoCompleteTerms !== null ? autoCompleteTerms[index] : []}
                                            dataListId={`suggestions-pesquisa-avancada-${index}`} />
                                    </Col>
                                    <Col
                                        sm="3"
                                        xs="12"
                                        className="mt-3 input-holder align-self-end"
                                    >

                                        {
                                            // eslint-disable-next-line no-nested-ternary
                                            index < 2 ? (
                                                index === items.length -1 ?  (
                                                    <div className="d-inline-block">
                                                        <button
                                                            type="button"
                                                            className="btn-generic--segundo ml-0 "
                                                            onClick={() => append({
                                                                input: '',
                                                                condition: 'AND',
                                                                option: 'LIVRE'
                                                            })}
                                                        >
                                                            <FontAwesomeIcon
                                                                icon="plus"
                                                                className="mr-2" />{' '}
                                                            {t('AdicionarTermo')}
                                                        </button>
                                                    </div>) : (
                                                    <>
                                                        <div className="rotulo">{t('Condicionais')}</div>
                                                        <Controller
                                                            control={control}
                                                            name={`items.${index}.condition`}
                                                            render={({ field }) => (
                                                                <FormControl
                                                                    as="select"
                                                                    className="form-control"
                                                                    {...field}
                                                                    isInvalid={errors?.items?.[index]?.condition !== undefined}
                                                                    style={{
                                                                        width: '70%',
                                                                        display: 'inline',
                                                                    }}
                                                                >
                                                                    <option value="-1" disabled />
                                                                    {
                                                                        conditions.map((condicao) => (
                                                                            <option
                                                                                key={`cond-${condicao.value}`}
                                                                                value={condicao.value}
                                                                            >
                                                                                {condicao.label}
                                                                            </option>
                                                                        ))
                                                                    }
                                                                </FormControl>
                                                            )}
                                                        />
                                                        {errors.items?.[index]?.condition  ? <FormControl.Feedback type="invalid">{t(errors.items[index].condition.message as any)}</FormControl.Feedback> : ''}
                                                    </>
                                                )
                                            ) : ''
                                        }
                                    </Col>
                                </Row>
                            </div>
                        ))
                    }
                </Col>
                <Col
                    md="2"
                    xs="12"
                    className={classnames(' mt-3 align-self-end pb-0', { 'mb-4': error })}
                >
                    <Button
                        title="Pesquisar"
                        icon="search"
                        variant='primeiro ml-0 btn-responsivo'
                        type="submit"
                        disabled={loading}
                    />
                </Col>
            </Row>
            <Row className="mt-3 ml-2 px-3">
                {/* @ts-ignore */}
                <Col sm={{ size: 11, offset: 1 }} xs="12">
                    <FormGroup tag="fieldset">
                        <span className="cortxt s14 f500 r10">
                            {t('TipoTermos')}:
                        </span>
                        <div className="d-lg-inline ml-lg-5 d-md-block mt-2">
                            <Label check className="preto70 s14 hover1">
                                <Controller
                                    control={control}
                                    render={({ field }) => (
                                        <Input
                                            {...field}
                                            type="radio"
                                            className="checkNovo"
                                            value="P"
                                            defaultChecked={field.value === "P"}
                                        />
                                    )}
                                    name="termType"
                                />
                                {t('PalavraLivre')}
                            </Label>
                        </div>
                        <div className="d-lg-inline ml-lg-5 d-md-block mt-2">
                            <Label check className="preto70 s14 hover1">
                                <Controller
                                    control={control}
                                    render={({ field }) => (
                                        <Input
                                            {...field}
                                            type="radio"
                                            className="checkNovo"
                                            value="E"
                                            defaultChecked={field.value === "E"}
                                        />
                                    )}
                                    name="termType"
                                />
                                {t('ExatamenteIgual')}
                            </Label>
                        </div>
                        <div className="d-lg-inline ml-lg-5 d-md-block mt-2">
                            <Label check className="preto70 s14 hover1">
                                <Controller
                                    control={control}
                                    render={({ field }) => (
                                        <Input
                                            {...field}
                                            type="radio"
                                            className="checkNovo"
                                            value="I"
                                            defaultChecked={field.value === "I"}
                                        />
                                    )}
                                    name="termType"
                                />
                                {t('TermoInicial')}
                            </Label>
                        </div>
                    </FormGroup>
                </Col>
                <Col className="d-flex justify-content-center align-items-center p-2" md="12">
                    <button
                        type="button"
                        className="btn-generic--segundo ml-0 "
                        onClick={_ => setIsOpenFilters(!isOpenFilters)}
                    >
                        <FontAwesomeIcon
                            icon={(isOpenFilters ? faMinus : faPlus) as IconProp}
                            className="mr-2" />{' '}
                        {t(
                            isOpenFilters ? 'MenosOpcoesDeConsulta' : 'MaisOpcoesDeConsulta'
                        )}
                    </button>
                </Col>
                <Col className='px-3' md="12">
                    <AdvancedSearchFilters
                        input={filtersOptionsInitialData}
                        onChange={(data: any) => {
                            setFilterOptions(data)
                        }}
                        isOpen={isOpenFilters}
                        setIsOpen={setIsOpenFilters}
                    />
                </Col>
            </Row>
        </Form>)
}

export default AdvancedSearch
